import { useState, useEffect, useCallback, useRef } from 'react';
import { carrierService } from '../services/carrierService';
import { transformCarrierForDisplay, transformCarrierForAPI } from '../utils/transformers';
import { parseApiError } from '../utils/errorUtils';

// Minimum time to show loading skeleton (prevents flash)
const MIN_LOADING_DURATION = 400;

export function useCarriers(initialParams) {
  // Use ref to ensure stable params reference across renders
  const paramsRef = useRef(initialParams || {});

  const [carriers, setCarriers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({ hasMore: false, nextKey: null });

  const fetchCarriers = useCallback(async (params = {}, signal) => {
    setIsLoading(true);
    setError(null);

    const startTime = Date.now();

    try {
      const response = await carrierService.list({ ...paramsRef.current, ...params, signal });

      // Validate response structure
      if (!response?.data?.carriers || !Array.isArray(response.data.carriers)) {
        throw new Error('Invalid response format from carriers API');
      }

      // Transform with error handling for individual items
      const transformed = response.data.carriers
        .map((carrier, index) => {
          try {
            return transformCarrierForDisplay(carrier);
          } catch (err) {
            console.error(`[useCarriers] Transform failed for carrier ${index}:`, err);
            return null;
          }
        })
        .filter(Boolean); // Remove failed transforms

      // Ensure minimum loading duration for smooth UX
      const elapsed = Date.now() - startTime;
      if (elapsed < MIN_LOADING_DURATION) {
        await new Promise(resolve => setTimeout(resolve, MIN_LOADING_DURATION - elapsed));
      }

      setCarriers(transformed);
      setPagination({
        hasMore: response.data.pagination?.has_more || false,
        nextKey: response.data.pagination?.next_key || null,
      });
    } catch (err) {
      if (err.name === 'AbortError' || err.name === 'CanceledError') {
        return; // Ignore abort errors
      }
      console.error('[useCarriers] Fetch failed:', err);
      setError(parseApiError(err));
    } finally {
      setIsLoading(false);
    }
  }, []); // No dependencies - paramsRef is stable

  // Initial fetch with cleanup
  // Small delay to ensure React renders loading state before fetch starts
  useEffect(() => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      fetchCarriers({}, controller.signal);
    }, 10); // Small delay to allow loading state to render
    return () => {
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, [fetchCarriers]);

  const createCarrier = useCallback(async (formData) => {
    const apiData = transformCarrierForAPI(formData);
    const response = await carrierService.create(apiData);
    const newCarrier = response.data;

    // Optimistically add to local state
    const transformed = transformCarrierForDisplay(newCarrier);
    setCarriers(prev => [...prev, transformed]);

    // Refresh list in background (non-blocking)
    const controller = new AbortController();
    fetchCarriers({}, controller.signal).catch(() => {});

    return newCarrier;
  }, [fetchCarriers]);

  const updateCarrier = useCallback(async (id, formData) => {
    const apiData = transformCarrierForAPI(formData);
    const response = await carrierService.update(id, apiData);

    // Update in local state
    const transformed = transformCarrierForDisplay(response.data);
    setCarriers(prev => prev.map(c => c.id === id ? transformed : c));

    return response.data;
  }, []);

  const deleteCarrier = useCallback(async (id) => {
    await carrierService.delete(id);

    // Remove from local state
    setCarriers(prev => prev.filter(c => c.id !== id));
  }, []);

  const activateCarrier = useCallback(async (id) => {
    const response = await carrierService.activate(id);
    const transformed = transformCarrierForDisplay(response.data);
    setCarriers(prev => prev.map(c => c.id === id ? transformed : c));
    return response.data;
  }, []);

  const deactivateCarrier = useCallback(async (id) => {
    const response = await carrierService.deactivate(id);
    const transformed = transformCarrierForDisplay(response.data);
    setCarriers(prev => prev.map(c => c.id === id ? transformed : c));
    return response.data;
  }, []);

  const loadMore = useCallback(async () => {
    if (!pagination.nextKey || isLoading) return;

    const controller = new AbortController();
    try {
      const response = await carrierService.list({
        ...paramsRef.current,
        cursor: pagination.nextKey,
        signal: controller.signal
      });

      const transformed = response.data.carriers
        .map(transformCarrierForDisplay)
        .filter(Boolean);

      setCarriers(prev => [...prev, ...transformed]);
      setPagination({
        hasMore: response.data.pagination?.has_more || false,
        nextKey: response.data.pagination?.next_key || null,
      });
    } catch (err) {
      if (err.name !== 'AbortError') {
        setError(parseApiError(err));
      }
    }
  }, [pagination.nextKey, isLoading]);

  return {
    carriers,
    isLoading,
    error,
    pagination,
    refetch: () => fetchCarriers({}),
    createCarrier,
    updateCarrier,
    deleteCarrier,
    activateCarrier,
    deactivateCarrier,
    loadMore,
  };
}
